import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { IFormValues } from '../../../types';
import { DEFAULT_DOWN_PAYMENT_PERCENTAGE, DOWN_PAYMENT_VALUES } from '../constants';
import { calculateValueFromPercentage } from '../../../utils';
import { EFieldName } from '../../../constants';
import { debounce } from '@cian/ui-kit/internal/utils';
import { IFetchOffersProps } from '../../../hooks/useGetOffers';
import { useSelector } from 'react-redux';
import { selectFormDefaults } from '../../../selectors/calculator';

export const useFormCalculator = (fetchOffers: (params: IFetchOffersProps) => void) => {
  const initialFormValues = useSelector(selectFormDefaults);

  const {
    setValue,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<IFormValues>({
    defaultValues: initialFormValues,
  });

  const { downPayment, objectCost, loanTermYears } = watch();

  const onSubmit = useCallback(
    async (data: IFormValues) => {
      fetchOffers({ ...data });
    },
    [fetchOffers],
  );

  const downPaymentOptions = DOWN_PAYMENT_VALUES.map(option => {
    return {
      value: calculateValueFromPercentage(objectCost, Number(option)),
      label: `${option}%`,
      dataTestId: `down-payment-${option}`,
    };
  });

  useEffect(() => {
    setValue(EFieldName.DownPayment, calculateValueFromPercentage(objectCost, DEFAULT_DOWN_PAYMENT_PERCENTAGE));
  }, [objectCost, setValue]);

  const debouncedSubmit = useMemo(() => debounce(handleSubmit(onSubmit), 500), [handleSubmit, onSubmit]);

  const getHandlerByFieldName = (fieldName: EFieldName) => (value: number) => {
    setValue(fieldName, value);

    if (isValid) {
      debouncedSubmit();
    }
  };

  return { objectCost, downPayment, loanTermYears, downPaymentOptions, getHandlerByFieldName, isValid };
};
